import z from "zod";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import { sureApiCall } from "../axiosClient/sureApi";
import { zodParseWithLog } from "../apiErrorLogs";

const TransportReferenceCircuitLegApiSchema = z.object({
    id: z.number(),
    orderIndex: z.number(),
    applicableStages: z.string().nullable().optional(),
    transportLegReference: z.object({
        id: z.number(),
        code: z.string(),
        transportModeReference: z
            .object({
                id: z.number(),
                code: z.string(),
                family: z.enum(["land", "sea", "air"]).optional(),
                isRefrigerated: z.boolean().optional(),
                name: z.string().nullable().optional(),
            })
            .optional(),
        name: z.string().nullable().optional(),
        distanceKm: z.number().nullable().optional(),
        isRefrigerated: z.boolean(),
        originCountryCode: z.string().nullable().optional(),
        destinationCountryCode: z.string().nullable().optional(),
    }),
});

const TransportReferenceApiSchema = z.object({
    id: z.number(),
    code: z.string(),
    name: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    originCountryCode: z.string().nullable().optional(),
    destinationCountryCode: z.string().nullable().optional(),
    applicableStage: z.string().nullable().optional(),
    applicableCpcRegex: z.string().nullable().optional(),
    applicableOriginRegion: z.string().nullable().optional(),
    tags: z.array(z.string()).default([]),
    legs: z.array(TransportReferenceCircuitLegApiSchema).default([]),
});

export type TransportReference = z.infer<typeof TransportReferenceApiSchema>;

export const isTransportReferenceApplicableToStage = (reference: TransportReference, stage: string): boolean => {
    if (reference.applicableStage == null || reference.applicableStage.length === 0) {
        return true;
    }

    return reference.applicableStage.split("|").includes(stage);
};

type SearchOptions = {
    originCountryCode?: string;
    destinationCountryCode?: string;
    applicableStage?: string;
    applicableOriginRegion?: string;
    tag?: string;
    nameQuery?: string;
    enabled?: boolean;
};

export const useSearchTransportReferences = ({
    originCountryCode,
    destinationCountryCode,
    applicableStage,
    applicableOriginRegion,
    tag,
    nameQuery,
    enabled = true,
}: SearchOptions = {}) => {
    const { i18n } = useTranslation();

    const query = useQuery({
        enabled,
        cacheTime: 0,
        staleTime: 0,
        queryKey: [
            "searchTransportReferences",
            originCountryCode ?? null,
            destinationCountryCode ?? null,
            applicableStage ?? null,
            applicableOriginRegion ?? null,
            tag ?? null,
            nameQuery ?? null,
            i18n.language,
        ],
        queryFn: async () => {
            return await sureApiCall.get("/api/transport_circuit_references", {
                params: {
                    itemsPerPage: 200,
                    ...(originCountryCode ? { originCountryCode } : {}),
                    ...(destinationCountryCode ? { destinationCountryCode } : {}),
                    ...(applicableOriginRegion ? { applicableOriginRegion } : {}),
                    ...(tag ? { tags: tag } : {}),
                    ...(nameQuery ? { name: nameQuery } : {}),
                },
            });
        },
        select: (request) => {
            const members = request.data["hydra:member"] ?? request.data.member ?? [];
            const references = zodParseWithLog(
                members,
                TransportReferenceApiSchema.array(),
                "searchTransportReferences",
            );

            return applicableStage === undefined ? references : (
                    references.filter((reference) => isTransportReferenceApplicableToStage(reference, applicableStage))
                );
        },
    });

    return {
        transportReferences: query.data ?? [],
        ...query,
    };
};
